import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import {
  authorizeUrl,
  useConnections,
  useCreateConnection,
  useIdentities,
  usePeople,
  useSignals,
} from "../api/hooks";
import { Button, Chip, ErrorBox, Loading, Page } from "../components/ui";
import { metaFor } from "../lib/connectors";

export const Route = createFileRoute("/setup")({ component: Setup });

type State = "done" | "active" | "waiting";

/** Connect a connector that has no row yet: create it, then send the browser to the vendor. */
function useConnect(onError: (e: unknown) => void) {
  const create = useCreateConnection();
  const connections = useConnections();
  const [busy, setBusy] = useState<string | null>(null);
  return {
    busy,
    go: async (connector: string) => {
      setBusy(connector);
      try {
        const existing = connections.data?.find((c) => c.connector === connector && c.status !== "revoked");
        const id = existing?.id ?? (await create.mutateAsync({ connector, external_workspace_id: null })).id;
        window.location.assign(await authorizeUrl(id));
      } catch (e) {
        onError(e);
        setBusy(null);
      }
    },
  };
}

function Setup() {
  const connections = useConnections();
  const people = usePeople();
  const identities = useIdentities(false);
  const org = useSignals({ scope_type: "org", scope_key: "all", limit: 1 });
  const [error, setError] = useState<unknown>(null);
  const connect = useConnect(setError);

  if (connections.isPending || people.isPending) return <Page title="Set up"><Loading /></Page>;

  const live = (id: string) => connections.data?.find((c) => c.connector === id && c.status === "active");
  const peopleCount = people.data?.length ?? 0;
  const allIdentities = identities.data ?? [];
  const unmapped = allIdentities.filter((i) => i.person_key == null);
  const waiting = unmapped.reduce((n, i) => n + i.work_items, 0);
  const hasSignals = (org.data?.length ?? 0) > 0;

  // Each step is done, or the first not-done step is active, or it waits its turn.
  const done = [
    peopleCount > 0,
    !!live("github"),
    !!live("slack"),
    allIdentities.length > 0 && unmapped.length === 0,
    hasSignals,
  ];
  const activeIndex = done.findIndex((d) => !d);
  const stateOf = (i: number): State => (done[i] ? "done" : i === activeIndex ? "active" : "waiting");

  return (
    <Page
      title="Set up"
      sub="Four connections and one decision, in the order that works. This page follows along — each step ticks itself off as the data arrives."
    >
      {error != null && <div className="mb-4"><ErrorBox error={error} /></div>}

      <Step
        n={1}
        title="Bring in the people"
        state={stateOf(0)}
        summary={peopleCount > 0 ? `${peopleCount} people${live("deel") ? " · Deel" : ""}` : live("deel") ? "Deel connected · first sync pending" : undefined}
      >
        {live("deel") ? (
          <>
            <p>
              Deel is connected. The first sync runs within 15 minutes of connecting and brings in
              everyone with a work e-mail; this step ticks itself off when they arrive.
            </p>
            <Note>
              Nothing to do here. If no people have appeared after half an hour, open{" "}
              <Link to="/connections" className="link">Connections</Link> and look at Deel's sync runs.
            </Note>
          </>
        ) : (
          <>
            <p>
              Everything else hangs off this. Each other tool gives us accounts — a GitHub login, a Slack
              member — and we match those to people by work e-mail. With no people to match against, every
              pull request and message arrives belonging to nobody.
            </p>
            <p className="mt-2">
              Deel is the source of record. If you are not on Deel yet, a CSV does the same job and takes a
              minute.
            </p>
            <Actions>
              <Button disabled={connect.busy === "deel"} onClick={() => void connect.go("deel")}>
                {connect.busy === "deel" ? "Opening Deel…" : "Connect Deel →"}
              </Button>
              <Link to="/organisation" className="btn btn-light">Import a CSV instead</Link>
            </Actions>
            <Reads id="deel" />
          </>
        )}
      </Step>

      <Step
        n={2}
        title="Connect the engineering work"
        state={stateOf(1)}
        summary={live("github") ? "GitHub connected" : undefined}
      >
        <p>
          Pull requests, issues, reviews and check results become the work each person has open, how long
          it has been sitting, and what to raise in a 1:1.
        </p>
        <Note>
          Expect every GitHub account to arrive unmatched. GitHub does not give us a member's e-mail
          unless they have made it public, so we cannot place them automatically. Step 4 is where you do
          it by hand. This is normal, not a fault.
        </Note>
        <Actions>
          <Button disabled={connect.busy === "github"} onClick={() => void connect.go("github")}>
            {connect.busy === "github" ? "Opening GitHub…" : "Connect GitHub →"}
          </Button>
        </Actions>
        <Reads id="github" />
      </Step>

      <Step
        n={3}
        title="Connect the collaboration signal"
        state={stateOf(2)}
        summary={live("slack") ? "Slack connected" : undefined}
      >
        <p>
          How many messages each person sends per hour in public channels. That is what after-hours
          drift and meeting load are built from. No message text is ever read or stored.
        </p>
        <Note>
          Slack limits us to one request a minute, and one request covers one channel. A workspace with
          twenty public channels takes about twenty minutes for a full pass; two hundred takes a few
          hours. Nothing is broken if the first sync is slow, and Slack accounts should match themselves
          because Slack does give us e-mail addresses.
        </Note>
        <Actions>
          <Button disabled={connect.busy === "slack"} onClick={() => void connect.go("slack")}>
            {connect.busy === "slack" ? "Opening Slack…" : "Connect Slack →"}
          </Button>
        </Actions>
        <Reads id="slack" />
      </Step>

      <Step
        n={4}
        title="Say who is who"
        state={stateOf(3)}
        summary={
          allIdentities.length === 0
            ? undefined
            : unmapped.length === 0
              ? "every account matched"
              : `${unmapped.length} waiting`
        }
      >
        {allIdentities.length === 0 ? (
          <p>Nothing to do until a tool has synced at least once. The accounts it finds will appear here.</p>
        ) : (
          <>
            <p>
              {unmapped.length} account{unmapped.length === 1 ? "" : "s"} could not be matched to a person
              automatically
              {waiting > 0 ? `, and ${waiting} item${waiting === 1 ? "" : "s"} of work are waiting on the answer` : ""}.
              Until each is matched, that work belongs to nobody and stays out of Work, Performance and
              1:1 prep.
            </p>
            <ul className="mt-2 text-[13px] text-muted list-disc pl-5">
              {unmapped.slice(0, 5).map((i) => (
                <li key={`${i.connector}:${i.external_id}`}>
                  <span className="font-semibold text-ink">{i.display ?? i.external_id}</span> from {metaFor(i.connector).name}
                  {i.work_items > 0 ? ` · ${i.work_items} items` : ""}
                </li>
              ))}
              {unmapped.length > 5 && <li>and {unmapped.length - 5} more</li>}
            </ul>
          </>
        )}
        <Actions>
          <Link to="/organisation" className="btn btn-dark">Match them up →</Link>
        </Actions>
      </Step>

      <Step n={5} title="Wait for the first read" state={stateOf(4)} summary={hasSignals ? "signals are in" : undefined}>
        <p>
          Syncs run on their own from here. The overnight job turns what arrived into signals, and the
          dashboard fills in once it has a complete week.
        </p>
        <Note>
          Nothing will flag on day one. A change has to hold for two consecutive complete weeks before it
          counts as a change rather than a blip, which is the difference between this and a dashboard
          that cries wolf.
        </Note>
        <Actions>
          <Link to="/" className="btn btn-dark">Go to the dashboard →</Link>
          <Link to="/connections" className="btn btn-light">Watch the syncs</Link>
        </Actions>
      </Step>

      {done.every(Boolean) && (
        <div className="card mt-6">
          <h3 className="font-bold text-[15px]">Worth checking once, now that data is flowing</h3>
          <ul className="mt-2 text-[13px] text-muted list-disc pl-5 space-y-1">
            <li>Open <Link to="/work" className="link">Work</Link> and confirm the people you matched in step 4 now own their pull requests.</li>
            <li>Compare the issue count there against your repository. Equal, not double.</li>
            <li>On <Link to="/workforce" className="link">Workforce</Link>, teams under five people should not appear at all.</li>
            <li>Look anywhere for a message body or a meeting title. Finding one is a stop-everything bug.</li>
          </ul>
        </div>
      )}
    </Page>
  );
}

function Step({
  n,
  title,
  state,
  summary,
  children,
}: {
  n: number;
  title: string;
  state: State;
  summary?: string;
  children: ReactNode;
}) {
  return (
    <div className={`card mb-3 ${state === "waiting" ? "opacity-55" : ""}`}>
      <div className="flex items-center gap-3">
        <span
          className={`flex items-center justify-center w-6 h-6 rounded-full text-[12px] font-bold ${
            state === "done" ? "bg-green text-white" : state === "active" ? "bg-ink text-white" : "bg-tile text-muted"
          }`}
          aria-hidden
        >
          {state === "done" ? "✓" : n}
        </span>
        <h3 className="font-bold text-[15px] flex-1">{title}</h3>
        {summary && <Chip tone={state === "done" ? "good" : "neutral"}>{summary}</Chip>}
      </div>
      {state !== "done" && <div className="mt-3 text-[13px] text-muted max-w-[76ch]">{children}</div>}
    </div>
  );
}

function Actions({ children }: { children: ReactNode }) {
  return <div className="flex gap-2 items-center mt-4">{children}</div>;
}

function Note({ children }: { children: ReactNode }) {
  return <p className="mt-3 border-l-2 border-orange pl-3 text-[13px]">{children}</p>;
}

/** The same promise the Connections page makes, repeated where the decision is taken. */
function Reads({ id }: { id: string }) {
  const meta = metaFor(id);
  return (
    <p className="mt-3 text-[12px] text-muted">
      <b className="text-ink font-semibold">Reads</b> {meta.reads}. <b className="text-ink font-semibold">Never</b> {meta.never}.
    </p>
  );
}
